import { BigNumber, FixedNumber } from '@ethersproject/bignumber'
import { WeiPerEther } from '@ethersproject/constants'
import _toString from 'lodash/toString'
import { BLOCKS_PER_YEAR } from 'config'
import masterChefAbi from 'config/abi/masterchef.json'
import cakeVaultAbi from 'config/abi/cakeVaultV2.json'
import { useCallback, useMemo } from 'react'
import { useVaultPoolByKey } from 'state/pools/hooks'
import useSWRImmutable from 'swr/immutable'
import { getAddress, getMasterChefAddress } from 'utils/addressHelpers'
import { BIG_ZERO } from '@pancakeswap/utils/bigNumber'
import { CAKE, tokens } from '@pancakeswap/tokens'
import { ChainDefaultId, ChainId, Currency } from '@pancakeswap/sdk'
import { BOOST_WEIGHT, DURATION_FACTOR, MAX_LOCK_DURATION, VAULT_EMISSIONS } from 'config/constants/pools'
import { Flexible2Locked, IsCakeVault, IsLocked, VaultKey } from 'state/types'
import { poolsConfig } from 'config/constants'
import { multicallv2 } from '../utils/multicall'
import useBUSDPrice from './useBUSDPrice'

// default
const DEFAULT_PERFORMANCE_FEE_DECIMALS = 2

const PRECISION_FACTOR = BigNumber.from('1000000000000')

const getCakeFlexibleApy = (
  totalCakePoolEmissionPerYear: FixedNumber,
  pricePerFullShare: FixedNumber,
  totalShares: FixedNumber,
) => {
  // console.log('getCakeFlexibleApy', totalCakePoolEmissionPerYear, totalCakeInVault)
  return totalCakePoolEmissionPerYear
    .mulUnsafe(FixedNumber.from(WeiPerEther))
    .divUnsafe(pricePerFullShare)
    .divUnsafe(totalShares)
    .mulUnsafe(FixedNumber.from(100))
}

const getCakeFlexibleNewApy = (
  totalCakePoolEmissionPerYear: FixedNumber,
  totalCakeInVault: FixedNumber,
  // pricePerFullShare: FixedNumber,
  // totalShares: FixedNumber,
) => {
  // console.log('getCakeFlexibleApy', totalCakePoolEmissionPerYear, totalCakeInVault)
  return totalCakePoolEmissionPerYear
    .mulUnsafe(FixedNumber.from(WeiPerEther))
    // .divUnsafe(pricePerFullShare)
    // .divUnsafe(totalShares)
    .divUnsafe(totalCakeInVault) // .addUnsafe(totalCakePoolEmissionPerYear.mulUnsafe(FixedNumber.from(WeiPerEther))))
    .mulUnsafe(FixedNumber.from(100))
}

const getTokenFlexibleApy = (
  totalCakePoolEmissionPerYear: FixedNumber,
  totalShares: FixedNumber,
  tokenPrice: FixedNumber,
) => {
  // console.log('getTokenFlexibleApy', totalCakePoolEmissionPerYear, totalShares, tokenPrice, totalCakePoolEmissionPerYear
  // .divUnsafe(totalShares)
  // .divUnsafe(tokenPrice)
  // .mulUnsafe(FixedNumber.from(100)))
  return totalCakePoolEmissionPerYear
    .divUnsafe(totalShares)
    .divUnsafe(tokenPrice)
    .mulUnsafe(FixedNumber.from(100))
}

const _getBoostFactor = (boostWeight: BigNumber, duration: number, durationFactor: BigNumber) => {
  // console.log('_getBoostFactor', boostWeight, duration, durationFactor, FixedNumber.from(boostWeight)
  // .mulUnsafe(FixedNumber.from(Math.max(duration, 0)))
  // .divUnsafe(FixedNumber.from(durationFactor))
  // .divUnsafe(FixedNumber.from(PRECISION_FACTOR)))
  return FixedNumber.from(boostWeight)
    .mulUnsafe(FixedNumber.from(Math.max(duration, 0)))
    .divUnsafe(FixedNumber.from(durationFactor))
    .divUnsafe(FixedNumber.from(PRECISION_FACTOR))
}

const getLockedApy = (flexibleApy: string, boostFactor: FixedNumber) => {
  return FixedNumber.from(flexibleApy).mulUnsafe(boostFactor.addUnsafe(FixedNumber.from('1')))
}

// const cakePoolPID = 0

export function useVaultApy({ chainId, vaultKey = VaultKey.CakeVault, duration = MAX_LOCK_DURATION }: { chainId: ChainId, vaultKey?: VaultKey, duration?: number } = { chainId: ChainDefaultId }) {
  const {
    totalShares = BIG_ZERO,
    totalStakedAmount,
    totalCakeInVault,
    pricePerFullShare = BIG_ZERO,
    fees: { performanceFeeAsDecimal } = { performanceFeeAsDecimal: DEFAULT_PERFORMANCE_FEE_DECIMALS },
  } = useVaultPoolByKey(vaultKey)

  const masterChefAddress = getMasterChefAddress(chainId)
  const pricePerFullShareAsEtherBN = useMemo(() => FixedNumber.from(pricePerFullShare.toString()), [pricePerFullShare])
  const poolVault = poolsConfig.find(pool => pool.vaultKey===vaultKey)
  const stakingToken = Object.values(tokens[chainId]).find((token: any) => token.symbol===poolVault?.stakingToken?.symbol) as Currency
  const cakePriceUSD = useBUSDPrice(CAKE[chainId])
  const tokenPriceUSD = useBUSDPrice(stakingToken)
  const totalSharesAsEtherBN = useMemo(() => FixedNumber.from(totalShares.shiftedBy(18 - stakingToken.decimals).toString()), [totalShares, stakingToken])
  const tokenPrice = !IsCakeVault(vaultKey) && cakePriceUSD && tokenPriceUSD 
    ? FixedNumber.from(Number(Number(tokenPriceUSD.toFixed(18)) / Number(cakePriceUSD.toFixed(18))).toFixed(18)) 
    : undefined // usePriceByPairs(CAKE[chainId], IsCakeVault(vaultKey) ? undefined : stakingToken)
  // console.log(vaultKey, tokenPriceUSD?.toFixed(18), cakePriceUSD.toFixed(18), tokenPrice?.toString())
  
  const { data: poolsEmissionPerYear } = useSWRImmutable('masterChef-total-cake-pool-emission', async () => {
    // const cakeVaultAddress = getVaultPoolAddress(VaultKey.CakeVault)
    // const nineinchVaultAddress = getVaultPoolAddress(VaultKey.NineInchVault)
    const vaultPools = poolsConfig.filter(pool => pool.vaultKey && IsLocked(pool.vaultKey) && !!pool.contractAddress[chainId])
    const calls1 = vaultPools.map(pool => {
      return {
        address: getAddress(pool.contractAddress, chainId),
        name: 'bbcPoolPID',
      }
    })

    const poolIDs = (await multicallv2({
      abi: cakeVaultAbi,
      calls: calls1,
      chainId
    })).map(ret => ret[0])

    const calls2 = [
      {
        address: masterChefAddress,
        name: 'bbcPerBlock',
        params: [false],
      },
      {
        address: masterChefAddress,
        name: 'bbcPerBlock',
        params: [true],
      },
      {
        address: masterChefAddress,
        name: 'totalSpecialAllocPoint',
      },
      {
        address: masterChefAddress,
        name: 'totalRegularAllocPoint',
      },
      ...poolIDs.map(pid => ({
        address: masterChefAddress,
        name: 'poolInfo',
        params: [pid],
      })),
    ]

    const [[specialFarmsPerBlock], [regularFarmsPerBlock], [totalSpecialAllocPoint], [totalRegularAllocPoint], ...poolInfos] = await multicallv2({
      abi: masterChefAbi,
      calls: calls2,
      chainId
    })

    const totalEmissions = vaultPools.reduce((sum, pool) => sum + (VAULT_EMISSIONS[chainId][pool.vaultKey] ?? 0), 0)
    const poolsEmission = {}
    poolInfos.map((poolInfo, i) => {
      const poolSharesInSpecialFarms = FixedNumber.from(poolInfo.allocPoint).divUnsafe(
        FixedNumber.from(poolInfo.isRegular ? totalRegularAllocPoint : totalSpecialAllocPoint),
      )
      poolsEmission[vaultPools[i].vaultKey] = FixedNumber.from(poolInfo.isRegular ? regularFarmsPerBlock : specialFarmsPerBlock)
          .mulUnsafe(FixedNumber.from(BLOCKS_PER_YEAR))
          .mulUnsafe(poolSharesInSpecialFarms)
          .mulUnsafe(FixedNumber.from(VAULT_EMISSIONS[chainId][Flexible2Locked(vaultPools[i].vaultKey)]))
          .divUnsafe(FixedNumber.from(totalEmissions))
      return undefined
    })
    return poolsEmission
  })

  const totalCakePoolEmissionPerYear = poolsEmissionPerYear ? poolsEmissionPerYear[vaultKey] : FixedNumber.from(0)
  const totalStakedAmountEther = IsCakeVault(vaultKey) ? totalCakeInVault?.shiftedBy(18) : totalStakedAmount?.shiftedBy(18 - (stakingToken?.decimals ?? 18))
  // console.log(vaultKey, totalStakedAmountEther.toString())
  const flexibleApy = useMemo(
    () =>
      totalCakePoolEmissionPerYear &&
      (
        IsCakeVault(vaultKey) 
        ? !pricePerFullShareAsEtherBN.isZero() &&
          !totalSharesAsEtherBN.isZero() &&
          // getCakeFlexibleApy(totalCakePoolEmissionPerYear, FixedNumber.from(totalStakedAmountEther.toString())).toString() 
          getCakeFlexibleApy(totalCakePoolEmissionPerYear, pricePerFullShareAsEtherBN, totalSharesAsEtherBN).toString() 
        : !totalStakedAmount.eq(0) && 
          tokenPrice && !tokenPrice.isZero() && 
          getTokenFlexibleApy(totalCakePoolEmissionPerYear, FixedNumber.from(totalSharesAsEtherBN.toString()), tokenPrice).toString()
      ),
    [pricePerFullShareAsEtherBN, totalSharesAsEtherBN, totalStakedAmount, tokenPrice],
  )

  // todo - fix this
  // check if duration is infinity and set to max duration
  if(duration === Infinity) {
    duration = MAX_LOCK_DURATION
  }

  const boostFactor = useMemo(() => _getBoostFactor(BOOST_WEIGHT, duration, DURATION_FACTOR), [duration])

  const lockedApy = useMemo(() => {
    return flexibleApy && getLockedApy(flexibleApy, boostFactor).toString()
  }, [boostFactor, flexibleApy])

  const getBoostFactor = useCallback(
    (adjustDuration: number) => _getBoostFactor(BOOST_WEIGHT, adjustDuration, DURATION_FACTOR),
    [],
  )

  const flexibleApyNoFee = useMemo(() => {
    if (flexibleApy && performanceFeeAsDecimal) {
      const rewardPercentageNoFee = _toString(1 - performanceFeeAsDecimal / 100)

      return FixedNumber.from(flexibleApy).mulUnsafe(FixedNumber.from(rewardPercentageNoFee)).toString()
    }

    return flexibleApy
  }, [flexibleApy, performanceFeeAsDecimal])

  return {
    flexibleApy: flexibleApyNoFee,
    lockedApy,
    getLockedApy: useCallback(
      (adjustDuration: number) => flexibleApy && getLockedApy(flexibleApy, getBoostFactor(adjustDuration)).toString(),
      [flexibleApy, getBoostFactor],
    ),
    boostFactor: useMemo(() => boostFactor.addUnsafe(FixedNumber.from('1')), [boostFactor]),
    getBoostFactor: useCallback(
      (adjustDuration: number) => getBoostFactor(adjustDuration).addUnsafe(FixedNumber.from('1')),
      [getBoostFactor],
    ),
  }
}
